// ============================================================================
// CrewAhead Maritime Platform - Zustand Auth Store
// ============================================================================

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User } from '../lib/types';
import { STORAGE_KEYS } from '../lib/utils/constants';

// ─── Store State ─────────────────────────────────────────────────────────────

export interface AuthState {
  // ─── Data ─────────────────────────────────────────────────────────────────
  /** Current authenticated user */
  user: User | null;
  /** JWT access token */
  token: string | null;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether the store has been hydrated from storage */
  isHydrated: boolean;

  // ─── Actions ──────────────────────────────────────────────────────────────
  /** Set the current user */
  setUser: (user: User | null) => void;
  /** Set the access token */
  setToken: (token: string | null) => void;
  /** Set both user and token from a login response */
  setAuth: (user: User, token: string) => void;
  /** Log out and clear all auth state */
  logout: () => void;
  /** Hydrate store from localStorage (called on mount) */
  hydrate: () => void;
  /** Update partial user data without overwriting the whole object */
  updateUser: (updates: Partial<User>) => void;
  /** Mark the store as hydrated */
  setHydrated: (value: boolean) => void;
}

// ─── Auth Store ──────────────────────────────────────────────────────────────

/**
 * Zustand store for authentication state.
 *
 * Persists user and token to localStorage for session recovery.
 * The store is hydrated on app mount via the `hydrate` action.
 *
 * @example
 * ```tsx
 * const user = useAuthStore((state) => state.user);
 * const setUser = useAuthStore((state) => state.setUser);
 * const logout = useAuthStore((state) => state.logout);
 *
 * // Or use the selector shorthand
 * const { user, isAuthenticated } = useAuthStore();
 * ```
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // ─── Initial State ────────────────────────────────────────────────────
      user: null,
      token: null,
      isAuthenticated: false,
      isHydrated: false,

      // ─── Actions ──────────────────────────────────────────────────────────

      setUser: (user: User | null): void => {
        set({
          user,
          isAuthenticated: !!user,
        });
      },

      setToken: (token: string | null): void => {
        set({ token });
      },

      setAuth: (user: User, token: string): void => {
        set({
          user,
          token,
          isAuthenticated: true,
        });
      },

      logout: (): void => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });

        // Clear persisted storage
        try {
          localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.USER);
        } catch {
          // Ignore storage errors
        }
      },

      hydrate: (): void => {
        // Only hydrate once
        if (get().isHydrated) return;

        try {
          // Check for token in localStorage
          const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

          if (!token) {
            set({ isHydrated: true });
            return;
          }

          // Corrupted-session safeguard: an access token without a refresh
          // token can never be renewed and would run degraded until natural
          // expiry. Treat it as logged out and clear all auth state.
          const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
          if (!refreshToken) {
            console.warn(
              '[Auth] Access token found without a refresh token — clearing corrupted session'
            );
            localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.USER);
            if (typeof document !== 'undefined') {
              document.cookie = 'ca_at=;path=/;max-age=0;SameSite=Lax';
            }
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isHydrated: true,
            });
            return;
          }

          // Try to parse cached user
          const cachedUser = localStorage.getItem(STORAGE_KEYS.USER);
          const user: User | null = cachedUser ? JSON.parse(cachedUser) : null;

          set({
            token,
            user,
            isAuthenticated: !!user && !!token,
            isHydrated: true,
          });
        } catch {
          // If parsing fails, clear everything
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isHydrated: true,
          });
        }
      },

      updateUser: (updates: Partial<User>): void => {
        const currentUser = get().user;
        if (!currentUser) return;

        const updatedUser: User = { ...currentUser, ...updates };
        set({ user: updatedUser });
      },

      setHydrated: (value: boolean): void => {
        set({ isHydrated: value });
      },
    }),
    {
      name: 'crew-ahead-auth-store',
      storage: createJSONStorage(() => localStorage),
      // Only persist user and token
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      // Don't persist hydration status
      onRehydrateStorage: () => {
        return (state, error) => {
          if (!error && state) {
            state.setHydrated(true);
          }
        };
      },
    }
  )
);

// ─── Selectors ───────────────────────────────────────────────────────────────

/**
 * Selector to get the current user's role.
 */
export const selectUserRole = (state: AuthState): string | null =>
  state.user?.role ?? null;

/**
 * Selector to check if the user has a specific role.
 */
export const selectHasRole = (state: AuthState, role: string): boolean =>
  state.user?.role === role;

/**
 * Selector to check if the user is an employer.
 */
export const selectIsEmployer = (state: AuthState): boolean =>
  state.user?.role === 'employer';

/**
 * Selector to check if the user is a seafarer.
 */
export const selectIsSeafarer = (state: AuthState): boolean =>
  state.user?.role === 'seafarer';

/**
 * Selector to check if the user is shore staff.
 */
export const selectIsShoreStaff = (state: AuthState): boolean =>
  state.user?.role === 'shore';

/**
 * Selector to check if the user is an admin.
 */
export const selectIsAdmin = (state: AuthState): boolean =>
  state.user?.role === 'admin';

/**
 * Selector to get the user's full name.
 */
export const selectUserFullName = (state: AuthState): string | null =>
  state.user
    ? [state.user.firstName, state.user.lastName].filter(Boolean).join(' ') || state.user.email || null
    : null;

/**
 * Selector to get the user's avatar URL.
 */
export const selectUserAvatar = (state: AuthState): string | null =>
  state.user?.avatarUrl ?? null;
